/**
 * CDP SDK Transport
 * API communication with exponential backoff retry
 */

import type { EventPayload, RetryConfig } from './types';
import { DEFAULT_RETRY_CONFIG } from './types';
import { log, warn, delay, calculateBackoffDelay } from './utils';

// =============================================================================
// TYPES
// =============================================================================

export interface SendResult {
  success: boolean;
  status?: number;
  error?: string;
  retryable: boolean;
}

// =============================================================================
// SEND EVENT
// =============================================================================

/**
 * Send a single event to the API
 */
export async function sendEvent(
  apiHost: string,
  writeKey: string,
  payload: EventPayload
): Promise<SendResult> {
  // CORS-simple: key in the query, text/plain body, NO custom header.
  // See the note on sendBatch below — the same reasoning applies here.
  const url = `${apiHost}/v1/events?key=${encodeURIComponent(writeKey)}`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify(payload),
      keepalive: true,
    });

    if (response.ok) {
      log('Event sent successfully:', payload.type, payload.messageId);
      return { success: true, status: response.status, retryable: false };
    }

    // Handle non-ok responses
    const status = response.status;

    // Client errors (4xx) - don't retry
    if (status >= 400 && status < 500) {
      const errorText = await response.text().catch(() => '');
      warn('Client error sending event:', status, errorText);
      return {
        success: false,
        status,
        error: errorText,
        retryable: false,
      };
    }

    // Server errors (5xx) - retry
    warn('Server error sending event:', status);
    return {
      success: false,
      status,
      error: `Server error: ${status}`,
      retryable: true,
    };
  } catch (error) {
    // Network errors - retry
    const errorMessage = error instanceof Error ? error.message : 'Network error';
    warn('Network error sending event:', errorMessage);
    return {
      success: false,
      error: errorMessage,
      retryable: true,
    };
  }
}

/**
 * Send event with retry logic
 */
export async function sendEventWithRetry(
  apiHost: string,
  writeKey: string,
  payload: EventPayload,
  retryConfig: RetryConfig = DEFAULT_RETRY_CONFIG
): Promise<boolean> {
  let attempt = 0;

  while (attempt <= retryConfig.maxRetries) {
    const result = await sendEvent(apiHost, writeKey, payload);

    if (result.success) {
      return true;
    }

    // Don't retry non-retryable errors
    if (!result.retryable) {
      return false;
    }

    attempt++;

    // If we've exhausted retries, give up
    if (attempt > retryConfig.maxRetries) {
      warn(
        'Event failed after',
        retryConfig.maxRetries,
        'retries:',
        payload.type,
        payload.messageId
      );
      return false;
    }

    // Calculate backoff delay
    const backoffMs = calculateBackoffDelay(
      attempt - 1,
      retryConfig.baseDelayMs,
      retryConfig.maxDelayMs,
      retryConfig.backoffMultiplier
    );

    log('Retrying event in', Math.round(backoffMs), 'ms (attempt', attempt, ')');
    await delay(backoffMs);
  }

  return false;
}

// =============================================================================
// BEACON (for page unload)
// =============================================================================

/**
 * Send event using sendBeacon (for reliable delivery on page unload)
 * Note: sendBeacon doesn't support custom headers, so the API key must be passed
 * as a URL query parameter. This is a browser API limitation.
 *
 * ⚠️ This note used to end "fetch-based transports use the Authorization header
 * instead for better security". That is no longer true and was never much of a
 * security gain: as of 2026-08-24 sendEvent/sendBatch pass the write key the
 * same way, deliberately, so their requests stay CORS-simple. The write key is
 * public by construction (it ships in the customer's page source), and
 * api-key-auth.ts permits query keys on write surfaces only for that reason.
 */
export function sendBeacon(
  apiHost: string,
  writeKey: string,
  payload: EventPayload
): boolean {
  if (!navigator.sendBeacon) {
    warn('sendBeacon not supported');
    return false;
  }

  const url = `${apiHost}/v1/events?key=${encodeURIComponent(writeKey)}`;
  // text/plain keeps the beacon a CORS-SIMPLE request. An application/json
  // Blob forces a preflight, and during page teardown the browser sends the
  // OPTIONS but discards the queued POST — the beacon's whole survives-unload
  // guarantee only holds for preflight-free requests. (Prod edge measured
  // 32,134 preflights vs 167 delivered beacon POSTs in 3h, 2026-08-18.)
  const blob = new Blob([JSON.stringify(payload)], { type: 'text/plain;charset=UTF-8' });

  const success = navigator.sendBeacon(url, blob);

  if (success) {
    log('Event sent via beacon:', payload.type);
  } else {
    warn('Beacon send failed:', payload.type);
  }

  return success;
}

/**
 * Send a batch of events via sendBeacon (reliable delivery on page unload).
 * sendBeacon can't set headers, so the write key goes in the query string
 * (same limitation as the single-event beacon). Beacon payloads are size-
 * capped by the browser (~64KB), so callers should chunk to small batches.
 * Returns false if the browser refused the beacon (caller should then persist).
 */
export function sendBeaconBatch(
  apiHost: string,
  writeKey: string,
  payloads: EventPayload[]
): boolean {
  if (payloads.length === 0) return true;
  if (typeof navigator === 'undefined' || !navigator.sendBeacon) {
    warn('sendBeacon not supported');
    return false;
  }

  const url = `${apiHost}/v1/events/batch?key=${encodeURIComponent(writeKey)}`;
  // text/plain = CORS-simple = no preflight = the beacon actually survives
  // page teardown. See sendBeaconEvent above for the measured failure mode.
  const blob = new Blob([JSON.stringify({ batch: payloads })], { type: 'text/plain;charset=UTF-8' });
  const success = navigator.sendBeacon(url, blob);

  if (success) {
    log('Batch sent via beacon:', payloads.length, 'events');
  } else {
    warn('Beacon batch send failed:', payloads.length);
  }
  return success;
}

// =============================================================================
// BATCH SEND
// =============================================================================

/**
 * Send multiple events in a single batch
 */
export async function sendBatch(
  apiHost: string,
  writeKey: string,
  payloads: EventPayload[]
): Promise<SendResult> {
  if (payloads.length === 0) {
    return { success: true, retryable: false };
  }

  if (payloads.length > 100) {
    warn('Batch size exceeds limit (100), truncating');
    payloads = payloads.slice(0, 100);
  }

  // CORS-simple request: write key in the query, text/plain body, and — the
  // load-bearing part — NO Authorization header.
  //
  // Measured on the production edge 2026-08-24: 1 098 `OPTIONS /v1/events/batch`
  // against 5 393 POSTs, plus 45 OPTIONS against 14 POSTs on /v1/events — 14%
  // of every request the CDP edge serves was a browser asking permission. The
  // preflight cache is NOT the problem: Access-Control-Max-Age is 86400 and
  // verified live. The problem is that the request was never simple.
  //
  // ⚠️ Content-Type alone would have changed NOTHING. A custom `Authorization`
  // header makes a request non-simple on its own, so both had to go. The key
  // moves to the query string, which api-key-auth.ts accepts unconditionally
  // for `apiKeyAuth('write')` surfaces — and /v1/events (with /v1/events/batch
  // under it) is mounted exactly that way. That middleware's own comment sets
  // the policy: query keys are for WRITE surfaces only, because read keys must
  // not reach access logs; a write key is embedded in the customer's page
  // source by construction, so it is not a secret being newly exposed.
  //
  // Everything this depends on is already proven in production on these exact
  // two endpoints: the unload beacons (2026-08-18) and the consent POST
  // (2026-08-20) both send `?key=` + text/plain here and work. express.json is
  // mounted with type ['application/json','text/plain'], so the body still
  // parses. The server accepts BOTH forms, so bundles cached on customer sites
  // keep working unchanged — there is no flag day.
  //
  // Alternative considered and rejected: keep the key out of the URL by putting
  // it in the body. That needs a server-side auth change, and would make this
  // the third distinct key-passing convention in one file. Consistency with the
  // two shipped paths wins.
  const url = `${apiHost}/v1/events/batch?key=${encodeURIComponent(writeKey)}`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify({ batch: payloads }),
      keepalive: true,
    });

    if (response.ok) {
      log('Batch sent successfully:', payloads.length, 'events');
      return { success: true, status: response.status, retryable: false };
    }

    const status = response.status;

    if (status >= 400 && status < 500) {
      const errorText = await response.text().catch(() => '');
      warn('Client error sending batch:', status, errorText);
      return { success: false, status, error: errorText, retryable: false };
    }

    warn('Server error sending batch:', status);
    return { success: false, status, error: `Server error: ${status}`, retryable: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Network error';
    warn('Network error sending batch:', errorMessage);
    return { success: false, error: errorMessage, retryable: true };
  }
}

// =============================================================================
// CONSENT ENDPOINT
// =============================================================================

/**
 * Send consent preferences to GDPR endpoint
 */
export async function sendConsentToServer(
  apiHost: string,
  writeKey: string,
  anonymousId: string | null,
  userId: string | null,
  preferences: import('./types').ConsentPreferences,
  mode: 'implied' | 'explicit' = 'explicit'
): Promise<boolean> {
  // Key in the query + text/plain body = CORS-simple (no preflight) and
  // keepalive = survives the visitor leaving right after clicking "Accept".
  // Before 2026-08-20 this was an Authorization-header JSON POST without
  // keepalive: every load preflighted (51k OPTIONS/h on the edge) and a
  // consent given just before navigating away was lost.
  const url = `${apiHost}/v1/gdpr/consent?key=${encodeURIComponent(writeKey)}`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify({
        anonymousId,
        userId,
        preferences,
        mode,
      }),
      keepalive: true,
    });

    if (response.ok) {
      log('Consent sent to server');
      return true;
    }

    warn('Failed to send consent:', response.status);
    return false;
  } catch (error) {
    warn('Error sending consent:', error);
    return false;
  }
}
